import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserPlus, Mail, Lock, Loader2, CheckCircle2, Circle } from 'lucide-react';
import MarkdownModal from '../components/common/MarkdownModal';

const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Password validation checks
  const passwordChecks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };

  // Check if password meets all requirements
  const isPasswordValid = passwordChecks.minLength &&
                          passwordChecks.hasUppercase &&
                          passwordChecks.hasNumber;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate terms agreement
    if (!agreeToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy to register.');
      return;
    }

    // Validate password before submitting
    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements.');
      return;
    }

    setIsLoading(true);

    try {
      await register(email, password);
      navigate('/');
    } catch (err) {
      // Handle FastAPI validation errors (422) which have different structure
      let errorMessage = 'Registration failed. Please try again.';

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // FastAPI validation errors can be an array of error objects
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-teal-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 px-4 py-8">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-300/20 dark:bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-300/20 dark:bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-200/10 dark:bg-emerald-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-md w-full space-y-8 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl p-8 md:p-10 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800/50">
        {/* Header with logo */}
        <div className="text-center space-y-3">
          <div className="flex justify-center mb-2">
            <img src="/logo.svg" alt="manteo.ai" className="h-12" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Create Account</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Sign up and start exploring Earth data</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50/80 dark:bg-red-950/40 backdrop-blur-sm border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 text-sm font-semibold p-4 rounded-xl animate-in slide-in-from-top-2 duration-200">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <Mail size={18} />
              </div>
              <input
                type="email"
                required
                className="block w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 transition-colors"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <Lock size={18} />
                </div>
                <input
                  type="password"
                  required
                  className="block w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 transition-colors"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {password && (
                <div className="mt-3 p-4 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border-2 border-gray-200 dark:border-gray-700 animate-in slide-in-from-top-2 duration-200">
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                    Password Requirements:
                  </p>
                  <ul className="space-y-2.5">
                    <li className={`flex items-center gap-3 text-sm transition-colors duration-200 ${passwordChecks.minLength ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {passwordChecks.minLength ? (
                        <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Circle size={18} className="text-gray-400 flex-shrink-0" />
                      )}
                      <span>At least 8 characters</span>
                    </li>
                    <li className={`flex items-center gap-3 text-sm transition-colors duration-200 ${passwordChecks.hasNumber ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {passwordChecks.hasNumber ? (
                        <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Circle size={18} className="text-gray-400 flex-shrink-0" />
                      )}
                      <span>At least one number</span>
                    </li>
                    <li className={`flex items-center gap-3 text-sm transition-colors duration-200 ${passwordChecks.hasUppercase ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {passwordChecks.hasUppercase ? (
                        <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />
                      ) : (
                        <Circle size={18} className="text-gray-400 flex-shrink-0" />
                      )}
                      <span>At least one uppercase letter</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border-2 border-gray-200 dark:border-gray-700">
            <div className="flex items-center h-5 pt-0.5">
              <input
                id="agree-to-terms"
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="w-5 h-5 text-emerald-600 bg-white border-2 border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500/30 focus:ring-offset-0 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-emerald-600 cursor-pointer transition-all"
              />
            </div>
            <label htmlFor="agree-to-terms" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer leading-relaxed">
              I agree to the{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setTermsModalOpen(true);
                }}
                className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-semibold underline-offset-4 hover:underline transition-colors"
              >
                Terms of Service
              </button>
              {' '}and{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setPrivacyModalOpen(true);
                }}
                className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-semibold underline-offset-4 hover:underline transition-colors"
              >
                Privacy Policy
              </button>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading || !isPasswordValid || !agreeToTerms}
            className="group relative w-full flex justify-center items-center gap-2 py-4 px-6 text-sm font-bold rounded-xl text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-[1.02] active:scale-[0.98]"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>Creating account...</span>
              </>
            ) : (
              <>
                <UserPlus size={20} className="group-hover:scale-110 transition-transform" />
                <span>Create Account</span>
              </>
            )}
          </button>

          <div className="text-center pt-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors underline-offset-4 hover:underline"
              >
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>

      {/* Terms and Privacy Policy Links */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-[14px] text-gray-400 dark:text-gray-500">
          <button
            onClick={() => setTermsModalOpen(true)}
            className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
          >
            Terms
          </button>
          {' · '}
          <button
            onClick={() => setPrivacyModalOpen(true)}
            className="hover:text-gray-600 dark:hover:text-gray-400 transition-colors underline-offset-2 hover:underline"
          >
            Privacy
          </button>
        </p>
      </div>

      <MarkdownModal
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        markdownUrl="/terms-of-service.md"
      />

      <MarkdownModal
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        markdownUrl="/privacy-policy.md"
      />
    </div>
  );
};

export default RegisterPage;
